import { ChevronDown } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { PriorityGroup, GroupLocation } from '~/api';
import type { TableColumn, TableRow } from '~/components/ui/table';
import { RetryBanner } from '~/components/ui/RetryBanner';
import type {
  BuyerInventoryGroupLocationsQuery,
  Inventory,
} from '~/graphql/graphql';
import { formatLengthWithBasis, formatQuantityCell } from '~/utils';
import {
  statusFromWeeksCover,
  weeksCover,
  weeksCoverForInventory,
} from '~/utils/inventory';
import {
  getPlatformProductDimensions,
  type ProductDimensionsInches,
} from '~/utils/order';
import { facilityDisplayLabel } from '~/pages/settings/sections/facilityLabel';
import {
  buildCompareUrl,
  getActionButtons,
  getAddToBuyListButton,
  getCompareButton,
  getStockCell,
  getWeeksCovered,
  type TFn,
} from './inventoryCells';
import { ActionRow } from './ActionRow';
import { subRowTestId } from './priorityGroupView';

const dash = { value: '—' };

export type GroupRowContext = {
  t: TFn;
  navigate: (path: string) => void;
};

export type GroupLocationsResult =
  UseQueryResult<BuyerInventoryGroupLocationsQuery>;

export type GroupSummaryRowOptions = {
  groupIndex: number;
  isExpanded: boolean;
  onToggle: (groupId: string) => void;
  onAddToBuyList: (group: PriorityGroup) => void;
};

/**
 * A sub-row Inventory merged with the identity fields the shared renderers and
 * EditInventoryModal expect: the sub-row payload omits what the group already
 * carries — location comes from the row, product+length from the group.
 */
function mergeSubRowInventory(
  group: PriorityGroup,
  row: GroupLocation
): Inventory {
  return {
    ...row.inventory,
    location: row.location,
    platformProductsLength: group.platformProductsLength,
  } as Inventory;
}

function groupDimensions(group: PriorityGroup): ProductDimensionsInches {
  const { platformProductsLength } = group;
  return getPlatformProductDimensions(
    platformProductsLength.platformProduct,
    platformProductsLength.length,
    platformProductsLength.isNominal
  );
}

function targetCompareUrl(group: PriorityGroup): string {
  const { targetInventory: target, platformProductsLength: ppl } = group;
  return buildCompareUrl({
    inventoryId: target.id,
    platformProductLengthId: ppl.id,
    locationId: target.location.id,
    platformProductId: ppl.platformProduct.id,
  });
}

// Column-header sort replaces the default urgency ranking with a server-side sort on the
// group's own aggregate (see BuyerInventoryGroupSortJpql); the group's per-location sub-rows are
// never sortable — only the group summary row's columns are.
export function buildQueueColumns(t: TFn): TableColumn[] {
  return [
    { key: 'location', header: t('inventory.location'), isSortable: true },
    {
      key: 'product',
      header: t('inventory.product_length'),
      isSortable: true,
    },
    {
      key: 'stock',
      header: t('inventory.available_stock'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'onOrder', header: t('inventory.on_order'), isSortable: true },
    { key: 'requested', header: t('inventory.requested'), isSortable: true },
    { key: 'inbound', header: t('inventory.inbound'), isSortable: true },
    {
      key: 'usageRatePerWeek',
      width: 10,
      header: t('inventory.usage'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'weeksCovered',
      header: t('inventory.weeks_cover'),
      isSortable: true,
    },
    { key: 'status', header: t('inventory.status'), isSortable: false },
    {
      key: 'compare',
      header: t('inventory.action'),
      isSortable: false,
      justification: 'end',
      headerJustification: 'start',
      width: 12,
      stickTo: 'right',
    },
  ];
}

export function buildGroupSummaryRow(
  group: PriorityGroup,
  { t, navigate }: GroupRowContext,
  { groupIndex, isExpanded, onToggle, onAddToBuyList }: GroupSummaryRowOptions
): TableRow {
  const target = group.targetInventory;
  const targetConfig = target.buyerInventoryConfiguration;
  const weeksCovered = weeksCover({
    currentStock: group.totalStock,
    onOrderStock: group.totalOnOrderStock,
    inboundStock: group.totalInboundStock,
    usageRatePerWeek: group.avgUsageRatePerWeek,
    dimensions: groupDimensions(group),
  });
  const unit = group.totalStock.unit;
  const facilityLabel = facilityDisplayLabel(group.facility);
  const facilityPlace = [
    group.facility.address?.city,
    group.facility.address?.region,
  ]
    .filter(Boolean)
    .join(', ');

  return {
    id: { value: group.id },
    _editable: false,
    location: {
      value: (
        <button
          type="button"
          data-testid={`group-toggle-${groupIndex}`}
          aria-expanded={isExpanded}
          aria-label={`${facilityLabel}, ${group.platformProductsLength.platformProduct.name} ${
            group.platformProductsLength.lengthDisplayValue
              ? formatLengthWithBasis(
                  group.platformProductsLength.lengthDisplayValue,
                  group.platformProductsLength.isNominal
                )
              : ''
          }`}
          onClick={() => onToggle(group.id)}
          className="flex items-center gap-2 text-left font-semibold text-neut-900"
        >
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`transition-transform ${isExpanded ? '' : '-rotate-90'}`}
          />
          <span>
            {facilityLabel}
            {facilityPlace && (
              <span className="block text-xs font-normal text-neut-500">
                {facilityPlace}
              </span>
            )}
          </span>
        </button>
      ),
    },
    product: {
      value: (
        <div>
          <div className="flex items-center gap-2">
            <span>{group.platformProductsLength.platformProduct.name}</span>
            {group.platformProductsLength.lengthDisplayValue && (
              <span className="shrink-0 whitespace-nowrap rounded bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700">
                {formatLengthWithBasis(
                  group.platformProductsLength.lengthDisplayValue,
                  group.platformProductsLength.isNominal
                )}
              </span>
            )}
          </div>
          <div className="text-xs text-neut-500">
            {group.platformProductsLength.platformProduct.code}
          </div>
        </div>
      ),
    },
    stock: {
      value: getStockCell(group.totalAvailableStock.value, unit),
    },
    onOrder: { value: formatQuantityCell(group.totalOnOrderStock.value, unit) },
    requested: {
      value: formatQuantityCell(group.totalRequestedStock.value, unit),
    },
    inbound: { value: formatQuantityCell(group.totalInboundStock.value, unit) },
    weeksCovered: {
      value: getWeeksCovered(
        weeksCovered,
        targetConfig?.weeksCoveredBeforeUrgent ?? 0,
        targetConfig?.weeksCoveredBeforeMonitor ?? 0
      ),
    },
    usageRatePerWeek: {
      value: (
        <span>
          {group.avgUsageRatePerWeek.value} {group.avgUsageRatePerWeek.unit}/
          {t('inventory.week')}
        </span>
      ),
    },
    status: {
      value: statusFromWeeksCover(
        weeksCovered,
        targetConfig?.weeksCoveredBeforeUrgent ?? 0,
        targetConfig?.weeksCoveredBeforeMonitor ?? 0,
        t
      ),
    },
    compare: {
      value: (
        <ActionRow>
          {getAddToBuyListButton(
            `add-to-buy-list-group-${groupIndex}`,
            () => onAddToBuyList(group),
            t
          )}
          {getCompareButton(
            `view-group-${groupIndex}`,
            targetCompareUrl(group),
            t,
            navigate,
            { label: t('inventory.compare_buy'), variant: 'filled' }
          )}
        </ActionRow>
      ),
    },
  };
}

export function buildGroupDetailRows(
  group: PriorityGroup,
  result: GroupLocationsResult | undefined,
  onOpenEdit: (inv: Inventory, facilityId: string, pplId: string) => void,
  { t, navigate }: GroupRowContext
): TableRow[] {
  const target = group.targetInventory;
  const pplId = group.platformProductsLength.id;
  const groupCompareUrl = targetCompareUrl(group);
  // Detail rows carry a stable, expand-order-independent testid suffix —
  // they have no positional index, so subRowTestId keys them by group.
  const detailBase = { _detail: true, _editable: false };

  if (result?.error) {
    return [
      {
        ...detailBase,
        id: { value: `${group.id}-error` },
        _testIdSuffix: subRowTestId(group.id, -1),
        location: {
          value: (
            <RetryBanner
              message={t('inventory.group_locations_failed')}
              retryLabel={t('inventory.retry')}
              onRetry={() => void result.refetch()}
              className="pl-6"
            />
          ),
        },
      },
    ];
  }

  if (!result || result.isLoading) {
    return [
      {
        ...detailBase,
        id: { value: `${group.id}-loading` },
        _testIdSuffix: subRowTestId(group.id, -1),
        location: {
          value: (
            <span className="pl-6 text-sm text-neut-500">
              {t('inventory.loading')}
            </span>
          ),
        },
      },
    ];
  }

  return (result.data?.buyerInventoryGroupLocations ?? []).map(
    (row, subIndex) => {
      const suffix = subRowTestId(group.id, subIndex);
      const inv = row.inventory;
      const isTarget = row.location.id === target.location.id;
      const locationCell = (
        <span className="flex items-center gap-2 pl-6">
          <span
            aria-hidden="true"
            className="inline-block h-3 w-3 shrink-0 border-b border-l border-neut-300"
          />
          {row.location.name}
          {isTarget && (
            <span className="px-2 py-0.5 rounded text-xs font-medium text-error-700 bg-error-100 uppercase">
              {t('inventory.target_badge')}
            </span>
          )}
        </span>
      );

      if (!inv) {
        return {
          ...detailBase,
          id: { value: `notstocked-${group.id}-${row.location.id}` },
          _testIdSuffix: suffix,
          location: { value: locationCell },
          stock: dash,
          onOrder: dash,
          requested: dash,
          inbound: dash,
          weeksCovered: dash,
          usageRatePerWeek: dash,
          status: {
            value: (
              <span className="text-xs text-neut-500">
                {t('inventory.not_stocked')}
              </span>
            ),
          },
          // Not stocked here means no local inventory to compare — reuse the group target's deep link.
          compare: {
            value: getCompareButton(
              `view-inv-${suffix}`,
              groupCompareUrl,
              t,
              navigate,
              { label: t('inventory.compare_buy') }
            ),
          },
        };
      }

      const config = inv.buyerInventoryConfiguration;
      const usage = config?.usageRatePerWeek.value ?? 0;
      const rowWeeks = weeksCoverForInventory(inv, groupDimensions(group));
      const merged = mergeSubRowInventory(group, row);
      const rowUnit = inv.quantity.unit;

      return {
        ...detailBase,
        _editable: true,
        // Group-scoped so the same inventory urgent at two facilities keeps a
        // unique React key / inline-edit target under each expanded group.
        id: { value: `${group.id}::${inv.id}` },
        _testIdSuffix: suffix,
        location: { value: locationCell },
        stock: {
          value: getStockCell(inv.availableStock.value, rowUnit),
          editValue: inv.quantity.value,
        },
        onOrder: { value: formatQuantityCell(inv.onOrderStock.value, rowUnit) },
        requested: {
          value: formatQuantityCell(inv.requestedStock.value, rowUnit),
        },
        inbound: { value: formatQuantityCell(inv.inboundStock.value, rowUnit) },
        weeksCovered: {
          value: getWeeksCovered(
            rowWeeks,
            config?.weeksCoveredBeforeUrgent ?? 0,
            config?.weeksCoveredBeforeMonitor ?? 0
          ),
        },
        usageRatePerWeek: {
          value: (
            <span>
              {usage}{' '}
              {config?.usageRatePerWeek.unit ??
                group.platformProductsLength.platformProduct.standardUnitsCode}
              /{t('inventory.week')}
            </span>
          ),
        },
        status: {
          value: statusFromWeeksCover(
            rowWeeks,
            config?.weeksCoveredBeforeUrgent ?? 0,
            config?.weeksCoveredBeforeMonitor ?? 0,
            t
          ),
        },
        compare: {
          value: getActionButtons(merged, t, {
            testIdSuffix: suffix,
            navigate,
            onOpenEdit: (inventory) =>
              onOpenEdit(inventory, group.facility.id, pplId),
            compareOptions: { label: t('inventory.compare_buy') },
          }),
        },
      };
    }
  );
}
